import React, { useState, useRef, useEffect } from "react";
import { Controller, RegisterOptions } from "react-hook-form";
import { gsap } from "gsap";
import ProgressBar from "../Progressbar";
import { Button } from "../Buttons";
import { CloseIcon, UploadIcon } from "../Icons/iconImages";
import { storage } from "../../firebase/firebase";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { toast } from "../Toast/toast";
// Import your Button, Upload, CloseIcon, ProgressBar components accordingly

interface FileInputProps {
  name?: string;
  control: any;
  rules?: RegisterOptions & {
    allowedFileTypes?: string[];
    maxDimensions?: { width: number; height: number };
    maxFileSize?: number;
  };
}

function FileInput({ name = 'file', control, rules = {} }: FileInputProps) {
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  // GSAP refs for animations
  const previewImgRef = useRef<HTMLImageElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Animate the progress bar when uploading starts.
  useEffect(() => {
    if (uploading && progressBarRef.current) {
      gsap.fromTo(
        progressBarRef.current,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 }
      );
    }
  }, [uploading]);

  // Extract display options (with defaults) from rules.
  const allowedFileTypes = rules.allowedFileTypes || ["SVG", "PNG", "JPG", "GIF"];
  const maxDimensionsObj = rules.maxDimensions || { width: 800, height: 400 };
  const allowedFormatsText = allowedFileTypes.join(", ");
  const maxDimensionsText = ` (max. ${maxDimensionsObj.width}x${maxDimensionsObj.height}px)`;
  const maxFileSizeText = rules.maxFileSize
    ? `, Max size: ${(rules.maxFileSize / 1024).toFixed(1)} KB`
    : "";

  // trigger the upload simulation.
  // const triggerUploadSimulation = () => {
  //   setUploading(true);
  //   setUploadProgress(0);
  //   const progressObj = { value: 0 };
  //   gsap.to(progressObj, {
  //     duration: 1,
  //     value: 100,
  //     onUpdate: () => setUploadProgress(progressObj.value),
  //     onComplete: () => {
  //       setUploading(false);
  //       if (progressBarRef.current) {
  //         gsap.fromTo(
  //           progressBarRef.current,
  //           { opacity: 1 },
  //           { opacity: 0, duration: 0.1 }
  //         );
  //       }
  //     },
  //   });
  // };

  interface UploaderProps {
    file: File;
    field: {
      onChange: (url: string | null) => void;
    };
  }
  const uploader = async ({ file, field }: UploaderProps) => {
    setUploading(true);
    const storageRef = ref(storage, `files/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on("state_changed",
      null,
      (error) => {
        toast.error("Upload failed. Please try again.");
        setUploading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((url) => {
          field.onChange(url);
          setUploading(false);
        });
      }
    );
  };


  return (
    <div className="flex items-center justify-center w-full">
      <Controller
        name={name}
        control={control}
        rules={rules} // Also passes any react-hook-form validations.
        render={({ field, fieldState: { error } }) => (
          <div className="w-full">
            <label
              htmlFor="dropzone-file"
              className={`flex flex-col ${previewUrl ? "items-start" : "items-center"
                } justify-center w-full p-4 rounded-lg cursor-pointer ${localError || error ? "border border-red-300" : "border border-gray-300"
                }`}
            >
              {previewUrl ? (
                <div className="flex flex-row gap-3 items-center justify-start relative">
                  <img
                    ref={previewImgRef}
                    src={previewUrl}
                    alt="Preview"
                    loading="lazy"
                    onLoad={() => {
                      if (previewImgRef.current) {
                        gsap.fromTo(previewImgRef.current, { opacity: 0 }, { opacity: 1, duration: 0.5 });
                      }
                    }}
                    className="max-w-[40%] h-36 hover:shadow-md transition-shadow rounded-lg object-cover"
                  />
                  <div className="absolute top-[-10px] left-[-10px]">
                    <Button
                      shape="circle"
                      size="small"
                      icon={<CloseIcon className="w-2 h-2" />}
                      onClick={(e) => {
                        e.preventDefault();
                        setPreviewUrl(null);
                        setSelectedFile(null);
                        setImageDimensions(null);
                        setLocalError(null);
                        setUploadProgress(0); // Reset progress to 0
                        setUploading(false);  // Hide progress bar
                        field.onChange(null);
                      }}                                          
                    />
                  </div>
                  <p className="pt-1.5 text-sm text-gray-600">
                    <span className="font-semibold">{selectedFile?.name}</span>
                    <span className="px-1">
                      {imageDimensions?.width} x {imageDimensions?.height}
                    </span>
                    <span className="px-1">
                      ({((selectedFile?.size ?? 0) / 1024).toFixed(1)} KB)
                    </span>
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-1">
                  <div className="border border-[#E9EAEB] shadow-sm hover:shadow-md transition-shadow bg-white p-2 rounded-lg">
                    <UploadIcon className="w-6 h-6" />
                  </div>
                  <p className="pt-1.5 text-sm">
                    <span className="font-semibold text-green-500">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs">
                    {localError ? (
                      <span className="text-red-500">{localError}</span>
                    ) : (
                      <span className="text-gray-600">
                        {allowedFormatsText}
                        {maxDimensionsText}
                        {maxFileSizeText}
                      </span>
                    )}
                  </p>
                </div>
              )}
              <input
                {...field}
                value={""}
                onChange={(event) => {
                  const files = event.target.files;
                  if (!files) return;
                  const file = files[0];
                  if (!file) return;

                  setLocalError(null);

                  // Validate file type
                  const fileExtension = file.name.split('.').pop()?.toUpperCase();
                  if (allowedFileTypes.length > 0 && fileExtension && !allowedFileTypes.includes(fileExtension)) {
                    setLocalError(`Invalid file type. Allowed types: ${allowedFormatsText}`);
                    return;
                  }

                  // Validate file size
                  if (rules.maxFileSize && file.size > rules.maxFileSize) {
                    setLocalError(`File size exceeds ${(rules.maxFileSize / (1024 * 1024)).toFixed(1)} MB`);
                    return;
                  }

                  setSelectedFile(file);
                  const preview = URL.createObjectURL(file);
                  setPreviewUrl(preview);
                  const img = new Image();
                  img.onload = () => {
                    const width = img.naturalWidth;
                    const height = img.naturalHeight;

                    // Validate image dimensions
                    if (rules.maxDimensions) {
                      if (width > rules.maxDimensions.width || height > rules.maxDimensions.height) {
                        setLocalError(`Image dimensions exceed ${rules.maxDimensions.width}x${rules.maxDimensions.height}px`);
                        setPreviewUrl(null);
                        setSelectedFile(null);
                        setImageDimensions(null);
                        URL.revokeObjectURL(preview);
                        return;
                      }
                    }

                    setImageDimensions({ width, height });
                    uploader({ file, field }); // Upload file immediately
                    URL.revokeObjectURL(preview);
                  };
                  img.onerror = () => {
                    setLocalError('Failed to load image');
                    setPreviewUrl(null);
                    setSelectedFile(null);
                    URL.revokeObjectURL(preview);
                  };
                  img.src = preview;
                }}
                id="dropzone-file"
                type="file"
                className="hidden"
              />
            </label>
            {uploading && selectedFile && (
              <div ref={progressBarRef} className="w-full mt-2">
                {uploadProgress > 0 ? (
                  <ProgressBar
                    progress={uploadProgress}
                    totalValue={100}
                    label={({ progress, totalValue }: { progress: number; totalValue: number }) =>
                      `${Math.round(progress)}% uploaded`
                    }
                  />
                ) : (
                  <div className="h-2.5 bg-gray-200 rounded-full">
                    <div className="h-2.5 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                )}
                <p className="text-sm text-gray-600 mt-1">
                  {uploadProgress > 0 ? `${Math.round(uploadProgress)}% uploaded` : "Initializing upload"}
                </p>
              </div>
            )}
          </div>
        )}
      />
    </div>
  );
}



export default FileInput;